import React, { useState, useEffect } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import Alert from "@material-ui/lab/Alert";

import {
  Chat,
  People,
  Timer,
  Done as DoneIcon,
  Info as InfoIcon,
  FormatListBulletedSharp,
} from "@material-ui/icons";

import { useHistory } from "react-router-dom";
import api from "../../services/api";

import Chart from "./Chart";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import CardCounter from "../../components/Dashboard/CardCounter";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";

import useDashboard from "../../hooks/useDashboard";
import useCompanies from "../../hooks/useCompanies";

import OnboardingChecklist from "../../components/OnboardingChecklist";
import WelcomeModal from "../../components/WelcomeModal";
import GuidedTour, { TOUR_STORAGE_KEY } from "../../components/GuidedTour";

import { isArray } from "lodash";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: 260,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterLabel: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    whiteSpace: "nowrap",
  },
  subscriptionFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.disabled,
    paddingTop: theme.spacing(1),
  },
  taskWidget: {
    padding: theme.spacing(2),
    height: "100%",
  },
  taskItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  taskDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  taskText: {
    flex: 1,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  taskDate: {
    fontSize: "0.7rem",
    flexShrink: 0,
  },
  taskWidgetHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  taskEmptyState: {
    textAlign: "center",
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
  },
}));

const QUICK_FILTERS = [
  { key: "today",  label: "Hoje" },
  { key: "7",     label: "7 dias" },
  { key: "30",    label: "30 dias" },
  { key: "month", label: "Este mês" },
  { key: "custom", label: "Personalizado" },
];

const PRIORITY_COLORS = { high: "#e53935", medium: "#fb8c00", low: "#1e88e5" };

const Dashboard = () => {
  const classes = useStyles();
  const history = useHistory();

  const [counters, setCounters]         = useState({});
  const [attendants, setAttendants]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [companyDueDate, setCompanyDueDate] = useState(null);
  const [tasks, setTasks]               = useState([]);
  const [runTour, setRunTour]           = useState(false);

  const [quickFilter, setQuickFilter]   = useState("today");
  const [dateFrom, setDateFrom]         = useState(moment().format("YYYY-MM-DD"));
  const [dateTo, setDateTo]             = useState(moment().format("YYYY-MM-DD"));

  const { find }    = useDashboard();
  const { finding } = useCompanies();
  const companyId   = localStorage.getItem("companyId");

  useEffect(() => {
    loadCompany();
    fetchDataWithDates(moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  }, []);

  const loadCompany = async () => {
    try {
      const company = await finding(companyId);
      setCompanyDueDate(moment(company.dueDate));
    } catch (e) {}
  };

  const fetchDataWithDates = async (from, to) => {
    setLoading(true);
    try {
      const data = await find({ date_from: from, date_to: to });
      setCounters(data.counters);
      setAttendants(isArray(data.attendants) ? data.attendants : []);
    } catch (e) {
      toast.error("Erro ao carregar dados do dashboard.");
    }
    setLoading(false);
  };

  const applyQuickFilter = (key) => {
    setQuickFilter(key);
    if (key === "custom") return;

    const today = moment().format("YYYY-MM-DD");
    let from = today;

    if (key === "7")     from = moment().subtract(6, "days").format("YYYY-MM-DD");
    if (key === "30")    from = moment().subtract(29, "days").format("YYYY-MM-DD");
    if (key === "month") from = moment().startOf("month").format("YYYY-MM-DD");

    setDateFrom(from);
    setDateTo(today);
    fetchDataWithDates(from, today);
  };

  const handleCustomFilter = () => {
    if (!moment(dateFrom).isValid() || !moment(dateTo).isValid()) {
      toast.error("Preencha as datas corretamente.");
      return;
    }
    fetchDataWithDates(dateFrom, dateTo);
  };

  const formatTime = (minutes) =>
    moment().startOf("day").add(minutes, "minutes").format("HH[h] mm[m]");

  const daysUntilExpiry  = companyDueDate ? companyDueDate.diff(moment(), "days") : null;
  const showExpiryAlert  = daysUntilExpiry !== null && daysUntilExpiry <= 7;
  const showExpiryFooter = !showExpiryAlert && companyDueDate !== null;

  const now = moment();
  const today = moment.utc().startOf("day");
  const sevenDaysLater = today.clone().add(7, "days");
  const upcomingTasks = tasks
    .filter((t) => {
      if (!t.dueDate || t.completed) return false;
      const due = moment.utc(t.dueDate).startOf("day");
      return due.isBefore(sevenDaysLater);
    })
    .sort((a, b) => {
      const aOverdue = moment.utc(a.dueDate).startOf("day").isBefore(today);
      const bOverdue = moment.utc(b.dueDate).startOf("day").isBefore(today);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      return moment.utc(a.dueDate).diff(moment.utc(b.dueDate));
    })
    .slice(0, 6);
  const pendingCount = tasks.filter((t) => !t.completed).length;

  return (
    <div>
      <WelcomeModal onGuide={() => {
        // Pequeno delay para o modal fechar antes do tour iniciar
        setTimeout(() => setRunTour(true), 400);
      }} />
      <GuidedTour run={runTour} onFinish={() => setRunTour(false)} />

      <Container maxWidth="lg" className={classes.container}>
        <Grid container spacing={3}>

          {/* Checklist de primeiros passos */}
          <Grid item xs={12} data-tour="onboarding-checklist">
            <OnboardingChecklist counters={counters} />
          </Grid>

          {/* Alerta de vencimento — só quando ≤ 7 dias */}
          {showExpiryAlert && (
            <Grid item xs={12}>
              <Alert severity={daysUntilExpiry <= 0 ? "error" : "warning"}>
                {daysUntilExpiry <= 0
                  ? `Assinatura vencida em ${companyDueDate.format("DD/MM/YYYY")}. Entre em contato para renovar.`
                  : `Assinatura vence em ${daysUntilExpiry} dia(s) — ${companyDueDate.format("DD/MM/YYYY")}.`}
              </Alert>
            </Grid>
          )}

          {/* Filtros rápidos */}
          <Grid item xs={12}>
            <div className={classes.filterRow}>
              <Typography variant="body2" className={classes.filterLabel}>
                Período:
              </Typography>
              {QUICK_FILTERS.map(({ key, label }) => (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  clickable
                  onClick={() => applyQuickFilter(key)}
                  color={quickFilter === key ? "primary" : "default"}
                  variant={quickFilter === key ? "default" : "outlined"}
                />
              ))}
            </div>

            {/* Campos de data — só no modo personalizado */}
            {quickFilter === "custom" && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 8 }}>
                <TextField
                  label="Data Inicial"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  label="Data Final"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                />
                <ButtonWithSpinner
                  loading={loading}
                  onClick={handleCustomFilter}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  Filtrar
                </ButtonWithSpinner>
              </Box>
            )}
          </Grid>

          {/* KPIs — linha 1 */}
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<Chat fontSize="inherit" />}
              title="Pendentes"
              value={counters.supportPending}
              loading={loading}
              color="#f57c00"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<Chat fontSize="inherit" />}
              title="Em atendimento"
              value={counters.supportHappening}
              loading={loading}
              color="#1565c0"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<DoneIcon fontSize="inherit" />}
              title="Realizados"
              value={counters.supportFinished}
              loading={loading}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<People fontSize="inherit" />}
              title="Leads"
              value={counters.leads}
              loading={loading}
              color="#6a1b9a"
            />
          </Grid>

          {/* KPIs — linha 2 */}
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<Timer fontSize="inherit" />}
              title="T.M. de Atendimento"
              value={formatTime(counters.avgSupportTime)}
              loading={loading}
              color="#00695c"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <CardCounter
              icon={<Timer fontSize="inherit" />}
              title="T.M. de Espera"
              value={formatTime(counters.avgWaitTime)}
              loading={loading}
              color="#bf360c"
            />
          </Grid>

          {/* Gráfico */}
          <Grid item xs={12}>
            <Paper className={classes.fixedHeightPaper}>
              <Chart />
            </Paper>
          </Grid>

          {/* Tarefas da Semana */}
          <Grid item xs={12} md={4}>
            <Paper className={classes.taskWidget}>
              <div className={classes.taskWidgetHeader}>
                <div>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <FormatListBulletedSharp style={{ fontSize: 18, opacity: 0.7 }} />
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      Tarefas
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
                  </Typography>
                </div>
              </div>

              {upcomingTasks.length === 0 ? (
                <div className={classes.taskEmptyState}>
                  Nenhuma tarefa próxima
                </div>
              ) : (
                upcomingTasks.map((task) => {
                  const due = moment.utc(task.dueDate);
                  const isOverdue = due.startOf("day").isBefore(today);
                  const dateLabel = due.format("DD/MM");
                  const truncated = task.text.length > 35
                    ? task.text.substring(0, 35) + "…"
                    : task.text;
                  return (
                    <div key={task.id} className={classes.taskItem}>
                      <span
                        className={classes.taskDot}
                        style={{ backgroundColor: PRIORITY_COLORS[task.priority] || "#9e9e9e" }}
                      />
                      <span className={classes.taskText} title={task.text}>
                        {truncated}
                      </span>
                      <Chip
                        label={dateLabel}
                        size="small"
                        className={classes.taskDate}
                        style={isOverdue
                          ? { backgroundColor: "#e53935", color: "#fff", fontWeight: 700, height: 20 }
                          : { height: 20 }
                        }
                      />
                    </div>
                  );
                })
              )}

              <Box mt={1.5}>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => history.push("/todolist")}
                >
                  Ver todas
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Tabela de agentes */}
          {attendants.length > 0 && (
            <Grid item xs={12}>
              <TableAttendantsStatus attendants={attendants} loading={loading} />
            </Grid>
          )}

          {/* Rodapé sutil de assinatura */}
          {showExpiryFooter && (
            <Grid item xs={12}>
              <div className={classes.subscriptionFooter}>
                <InfoIcon style={{ fontSize: 14 }} />
                <Typography variant="caption">
                  Assinatura ativa até {companyDueDate.format("DD/MM/YYYY")}
                </Typography>
              </div>
            </Grid>
          )}

        </Grid>
      </Container>
    </div>
  );
};

export default Dashboard;
